/* eslint-disable react/no-array-index-key */
import * as React from "react";
import { css, cx } from "@emotion/css";
import lodash from "lodash";
import {
  BYOFundEntryInterface,
  BYOFundCustomPercentEntryInterface,
  PfFundSelectionBYOFieldError
} from "../support/interfaces/fundSelectionInterfaces";

import bootstrapStyles from "../css/bootstrap.module.css";
import formGroupStyles from "../css/formgroup.module.css";
import { PfFundSelectionBYOEntry } from "./PfFundSelectionBYOEntry";

interface PfFundSelectionBYOProps {
  funds: BYOFundEntryInterface[];
  maxFundNumber: number;
  fundChoices: BYOFundCustomPercentEntryInterface[];
  onAddFund: () => void;
  onFundSelectionChange: (
    fund: BYOFundEntryInterface | undefined,
    index: number
  ) => void;
  onPercentChange: (
    args0: React.FormEvent<HTMLInputElement>,
    args1: number
  ) => void;
  onRemoveEntry: (index: number) => void;
  setParentValid: (isValid: boolean) => void;
}

export const PfFundSelectionBYO = ({
  funds,
  maxFundNumber,
  fundChoices,
  onFundSelectionChange,
  onPercentChange,
  onRemoveEntry,
  onAddFund,
  setParentValid
}: PfFundSelectionBYOProps) => {
  const byoSelectionContainerClass = css`
    margin-top: 40px;
    margin-bottom: 40px;
  `;

  const byoFundTotalClass = css`
    font-size: 18px;
    font-weight: 700;
    color: #626767;
    margin: 0;
    padding: 0 0 10px 0;
  `;

  const addFundClass = css`
    padding: 0.7em;
    border-radius: 15px;
    text-transform: uppercase;
    font-size: 0.875em;
    cursor: pointer;
    border: none;
    color: #fff;
    background: #0069a6;
  `;

  const contentPadding = css`
    padding: 0 27px;
  `;

  const noPadding = css`
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  `;

  const [touched, setTouched] = React.useState(false);

  const selectedFundCodeList = fundChoices.map(
    fundchoice => fundchoice.fundCode
  );

  const totalPercent = (() => {
    if (fundChoices.length === 0) return 0;
    if (fundChoices.length === 1)
      return fundChoices[0].fundPercent ? fundChoices[0].fundPercent : 0;
    const sum = fundChoices
      .map(entry => (entry.fundPercent ? entry.fundPercent : 0))
      .reduce((total, num) => {
        return total + num;
      });
    return sum;
  })();

  const getTotalPercentError = React.useCallback((): PfFundSelectionBYOFieldError => {
    if (fundChoices.length === 0) {
      return {
        displayError: true,
        errorMessage: "You must select at least 1 fund."
      };
    }

    if (totalPercent !== 100)
      return {
        displayError: true,
        errorMessage: "This value must equal 100%."
      };

    return {
      displayError: false
    };
  }, [fundChoices.length, totalPercent]);

  React.useEffect(() => {
    const errors1 = fundChoices.map(fundChoice => !fundChoice.fundName);
    const errors2 = fundChoices.map(fundChoice => !fundChoice.fundPercent);
    const errors3 = [getTotalPercentError().displayError];
    const final = errors1.concat(errors2).concat(errors3);

    const overAllDisplayError = lodash.some(final, hasError => hasError);
    setParentValid(!overAllDisplayError);
  }, [fundChoices, getTotalPercentError, setParentValid]);

  return (
    <React.Fragment>
      <div className={bootstrapStyles.row}>
        <div className={cx(bootstrapStyles["col-md-12"], contentPadding)}>
          <div id="fundSelectionBuildYourOwnFundListTop">
            Select up to four funds below with an allocation percentage for each
            fund. If you do not know which fund(s) you&apos;d like you can{" "}
            <a
              href="https://www.americancentury.com/content/direct/en/products/all-funds/r.html#!/performance"
              target="_blank"
              rel="noopener noreferrer"
            >
              research and compare
            </a>{" "}
            funds.
          </div>
        </div>
      </div>

      <div className={bootstrapStyles.row}>
        <div
          className={cx(
            bootstrapStyles["col-md-10"],
            bootstrapStyles["col-md-offset-1"],
            byoSelectionContainerClass
          )}
        >
          {fundChoices.map((fundChoice, index) => {
            const fundError = {
              fundSelect: {
                displayError: !fundChoice.fundName,
                errorMessage: "This field is required."
              },

              fundPercent: {
                displayError: !fundChoice.fundPercent,
                errorMessage: "This field is required."
              }
            };
            return (
              <PfFundSelectionBYOEntry
                funds={funds}
                selectedFundCodeList={selectedFundCodeList}
                index={index}
                key={index}
                onFundSelectionChange={(
                  fund: BYOFundEntryInterface | undefined
                ) => {
                  onFundSelectionChange(fund, index);
                }}
                onPercentChange={e => {
                  onPercentChange(e, index);
                }}
                onRemoveEntry={() => {
                  onRemoveEntry(index);
                }}
                externalBindedValues={{
                  fundSelect: fundChoice,
                  fundPercent: fundChoice.fundPercent
                }}
                error={fundError}
                setParentTouched={() => {
                  setTouched(true);
                }}
              />
            );
          })}

          <div
            className={cx(
              bootstrapStyles.row,
              formGroupStyles["form-group"],
              noPadding
            )}
            style={{ marginTop: "15px" }}
          >
            <div className={bootstrapStyles["col-md-8"]}>
              {fundChoices.length < maxFundNumber && (
                <button
                  data-testid="addFundBtn"
                  className={addFundClass}
                  id="addFund"
                  type="button"
                  onClick={() => {
                    onAddFund();
                  }}
                >
                  Add Fund
                </button>
              )}
            </div>
            <div
              className={cx(
                bootstrapStyles["col-md-4"],
                formGroupStyles["form-group"],
                {
                  [formGroupStyles.error]:
                    getTotalPercentError().displayError && touched
                }
              )}
            >
              <p className={byoFundTotalClass}>Total: {totalPercent}</p>
              <div className={formGroupStyles["error-messages"]}>
                {touched && getTotalPercentError().errorMessage}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};
